/**
 * Press Page - Olympic Fit Wellness
 * Media coverage and press resources
 */
import { Link } from 'react-router-dom';
import { ArrowRight, Download, ExternalLink, Calendar } from 'lucide-react';

export default function PressPage() {
  const pressReleases = [
    {
      date: 'February 15, 2026',
      title: 'Olympic Fit Wellness Announces Expansion to 20 Locations by 2027',
      excerpt: 'OFW reveals plans to open 5 new premium wellness centers across Texas and Oklahoma.'
    },
    {
      date: 'January 8, 2026',
      title: 'OFW Launches Revolutionary Mental Wellness Program',
      excerpt: 'New comprehensive mental health initiative integrates mindfulness, therapy, and fitness.'
    },
    {
      date: 'December 1, 2025',
      title: 'Olympic Fit Wellness Named "Best Gym in DFW" for Third Consecutive Year',
      excerpt: 'Dallas Morning News readers vote OFW as the top fitness destination in the metroplex.'
    },
    {
      date: 'October 15, 2025',
      title: 'Founders Rae Edwards and Clint Fuqua Featured in Forbes',
      excerpt: 'Forbes profiles the dynamic duo revolutionizing the wellness industry.'
    }
  ];

  const mediaFeatures = [
    {
      outlet: 'Forbes',
      title: '"The Future of Fitness is Comprehensive Wellness"',
      image: 'https://images.unsplash.com/photo-1614028674026-a65e31bfd27c?w=300'
    },
    {
      outlet: 'Dallas Morning News',
      title: '"Olympic Fit Wellness Leads DFW Fitness Revolution"',
      image: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=300'
    },
    {
      outlet: 'Inc. Magazine',
      title: '"How OFW Built a Wellness Empire"',
      image: 'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=300'
    }
  ];

  return (
    <div className="bg-white" data-testid="press-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">MEDIA</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-normal text-white mb-6 tracking-[0.04em] uppercase">
            Press & Media
          </h1>
          <p className="text-base md:text-lg text-neutral-400 max-w-2xl mx-auto tracking-wide">
            News, press releases, and media resources about Olympic Fit Wellness.
          </p>
        </div>
      </section>

      {/* Media Contact */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">MEDIA CONTACT</span>
              <h2 className="text-3xl md:text-4xl font-heading font-normal text-neutral-900 mb-6 tracking-[0.04em] uppercase">
                Press Inquiries
              </h2>
              <p className="text-neutral-600 mb-8 tracking-wide leading-relaxed">
                For press inquiries, interview requests, or media resources, please contact our communications team.
              </p>
              <div className="space-y-4">
                <p className="text-neutral-700">
                  <strong>Media Relations</strong><br/>
                  press@olympicfitwellness.com
                </p>
                <p className="text-neutral-700">
                  <strong>Phone</strong><br/>
                  (469) 555-0101
                </p>
              </div>
            </div>
            
            <div className="bg-neutral-50 p-8">
              <h3 className="text-xl font-heading font-normal text-neutral-900 mb-6 tracking-wide">Press Kit</h3>
              <p className="text-neutral-600 mb-6">
                Download our press kit for logos, brand guidelines, founder bios, and high-resolution images.
              </p>
              <button className="inline-flex items-center gap-2 bg-black text-white font-heading px-8 py-4 text-sm tracking-[0.08em] hover:bg-neutral-800 transition-colors">
                <Download className="w-4 h-4" />
                DOWNLOAD PRESS KIT
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Press Releases */}
      <section className="py-16 lg:py-24 bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">NEWS</span>
            <h2 className="text-3xl md:text-4xl font-heading font-normal text-neutral-900 mb-6 tracking-[0.04em] uppercase">
              Press Releases
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {pressReleases.map((release, idx) => (
              <div 
                key={idx} 
                className="bg-white p-8 border border-neutral-200 hover:border-neutral-400 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2 text-sm text-neutral-500 mb-3">
                  <Calendar className="w-4 h-4" />
                  {release.date}
                </div>
                <h3 className="text-xl font-heading font-normal text-neutral-900 mb-3 tracking-wide">
                  {release.title}
                </h3>
                <p className="text-neutral-600 mb-4">{release.excerpt}</p>
                <span className="inline-flex items-center gap-2 text-sm font-heading tracking-[0.06em] text-neutral-900 hover:text-neutral-600 transition-colors">
                  READ MORE <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Media Features */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">IN THE NEWS</span>
            <h2 className="text-3xl md:text-4xl font-heading font-normal text-neutral-900 mb-6 tracking-[0.04em] uppercase">
              Media Coverage
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mediaFeatures.map((feature, idx) => (
              <div key={idx} className="group cursor-pointer">
                <div className="aspect-[4/3] overflow-hidden mb-4 bg-neutral-100">
                  <img 
                    src={feature.image}
                    alt={feature.outlet}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <p className="text-sm text-neutral-500 mb-2 tracking-wider uppercase">{feature.outlet}</p>
                <h3 className="text-lg font-heading font-normal text-neutral-900 tracking-wide group-hover:text-neutral-600 transition-colors">
                  {feature.title}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Facts */}
      <section className="py-16 lg:py-24 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">AT A GLANCE</span>
            <h2 className="text-3xl md:text-4xl font-heading font-normal text-white mb-6 tracking-[0.04em] uppercase">
              Company Facts
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '2015', label: 'Founded' },
              { value: '15+', label: 'Locations' },
              { value: '50,000+', label: 'Members' },
              { value: '500+', label: 'Team Members' }
            ].map((stat, idx) => (
              <div key={idx}>
                <div className="text-4xl md:text-5xl font-heading font-normal text-white mb-2">{stat.value}</div>
                <div className="text-sm text-neutral-500 tracking-wider uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
